import { FC, MouseEvent, useMemo, useState } from 'react';
import { Dot } from '@connect-x-app/hybrid-ui';
import { IconName } from '@connect-x-app/shared-types';
import { ColorName, icons } from '@connect-x-app/shared-ui';
import classnames from 'classnames';

import './Icon.scss';

type Props = {
  badgeText?: string | number;
  className?: string;
  colorName?: ColorName;
  disabled?: boolean;
  hoverColorName?: ColorName;
  label?: string;
  name: IconName;
  onClick?: (event: MouseEvent) => void;
  onHover?: (event: MouseEvent) => CallableFunction;
  showBadge?: boolean;
  size?: number | null;
  testId?: string;
};

export const Icon: FC<Props> = ({
  badgeText,
  className = '',
  colorName,
  disabled,
  hoverColorName,
  label,
  name,
  onClick,
  onHover,
  showBadge,
  size = 2,
  testId,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const Svg = icons[name];

  const iconColor = useMemo(
    () => (hoverColorName && isHovered ? hoverColorName : colorName),
    [colorName, hoverColorName, isHovered],
  );

  if (!Svg) {
    return null;
  }

  const sizeObject = size ? { height: `${size}rem`, width: `${size}rem` } : {};

  const handles: {
    onClick?: (value: MouseEvent) => void;
    onMouseEnter?: (value: MouseEvent) => void;
    onMouseLeave?: () => CallableFunction | void;
  } = {};

  handles.onClick = e => {
    if (!disabled) {
      onClick?.(e);
      setIsHovered(false);
    }
  };

  handles.onMouseEnter = e => {
    onHover?.(e);
    setIsHovered(true);
  };

  handles.onMouseLeave = () => {
    onHover?.({} as MouseEvent);
    setIsHovered(false);
  };

  const isIconOnly = !onClick;

  const renderIcon = (testId?: string) => {
    return (
      <i
        {...(isIconOnly ? handles : null)}
        className={classnames(['icon', name, isIconOnly && className, { 'custom-color': !!iconColor }])}
        data-test={testId}
        style={{ ...sizeObject, color: iconColor }}
      >
        <Svg />
      </i>
    );
  };

  const renderButton = (handlesEnabled = true) => {
    const handleButtonComponent = handlesEnabled ? handles : {};

    return (
      <button
        aria-label={label}
        className={classnames('plain', className)}
        data-test={testId}
        disabled={disabled}
        name={label}
        {...handleButtonComponent}
        style={sizeObject}
        type="button"
      >
        {renderIcon()}
      </button>
    );
  };

  if (isIconOnly) {
    return renderIcon(testId);
  }

  if (showBadge) {
    return (
      <div {...handles} className="icon-unread" role="button">
        {renderButton(false)}
        <Dot
          className={classnames(
            'icon-unread__dot',
            badgeText ? 'icon-unread__dot--small' : 'icon-unread__dot--extra-extra-small',
          )}
          fontSize="XS"
          size={badgeText ? 's' : 'xxs'}
          value={badgeText}
        />
      </div>
    );
  }

  return renderButton();
};
